import { createApp } from 'vue'
import './style.css'
import App from './App.vue'
import router from './router'
import APP_CONFIG from './config/app'
import { logger } from './utils/logger'

/**
 * Initialisiert das Theme früh, um FOUT (Flash of Unstyled Theme) zu vermeiden
 * Muss vor dem Mount der App ausgeführt werden
 */
function initializeTheme() {
  const STORAGE_KEY = 'portfolio-theme'
  const THEME_ATTRIBUTE = 'data-theme'
  
  // Lade gespeichertes Theme aus LocalStorage
  const stored = localStorage.getItem(STORAGE_KEY) as 'light' | 'dark' | null
  
  let theme: 'light' | 'dark' = 'light'
  
  if (stored && (stored === 'light' || stored === 'dark')) {
    theme = stored
  } else {
    // System-Präferenz verwenden
    if (typeof window !== 'undefined') {
      theme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
    }
  }
  
  // Wende Theme sofort an
  if (typeof document !== 'undefined') {
    const html = document.documentElement
    // Stelle sicher, dass die dark-Klasse korrekt gesetzt wird
    if (theme === 'dark') {
      html.classList.add('dark')
      html.setAttribute(THEME_ATTRIBUTE, 'dark')
    } else {
      // Entferne die dark-Klasse explizit
      html.classList.remove('dark')
      html.setAttribute(THEME_ATTRIBUTE, 'light')
      // Force reflow um sicherzustellen, dass Tailwind die Änderung erkennt
      void html.offsetHeight
    }
  }
}

/**
 * Setzt das Favicon im <head> der Seite
 */
function setFavicon(url: string) {
  if (typeof document === 'undefined') return

  // Entferne vorhandene Favicon-Links
  const existingLinks = document.querySelectorAll('link[rel="icon"], link[rel="shortcut icon"]')
  existingLinks.forEach(link => link.remove())

  // Bestimme den MIME-Type basierend auf der URL
  const extension = url.split('.').pop()?.toLowerCase()
  let type = 'image/x-icon'
  
  if (extension === 'svg') {
    type = 'image/svg+xml'
  } else if (extension === 'png') {
    type = 'image/png'
  } else if (extension === 'webp') {
    type = 'image/webp'
  }

  // Erstelle neuen Favicon-Link
  const link = document.createElement('link')
  link.rel = 'icon'
  link.type = type
  link.href = url
  document.head.appendChild(link)

  // Füge auch shortcut icon für ältere Browser hinzu
  const shortcutLink = document.createElement('link')
  shortcutLink.rel = 'shortcut icon'
  shortcutLink.type = type
  shortcutLink.href = url
  document.head.appendChild(shortcutLink)
}

// Initialisiere Theme vor dem Mount
initializeTheme()

/**
 * Unterdrückt Firebase-spezifische Fetch-Logs in der Konsole
 * Diese Logs sind normal und werden von Firebase's WebChannel-Bibliothek generiert
 */
function suppressFirebaseLogs() {
  if (typeof window === 'undefined') return
  
  const originalConsoleError = console.error
  const originalConsoleWarn = console.warn
  
  // Filtere Firebase WebChannel-Logs
  console.error = (...args: unknown[]) => {
    const message = args[0]?.toString() || ''
    // Unterdrücke Firebase WebChannel Fetch-Logs
    if (
      message.includes('webchannel') ||
      message.includes('firestore.googleapis.com') ||
      message.includes('Fetch failed loading') ||
      message.includes('Fetch finished loading')
    ) {
      return // Unterdrücke diese Logs
    }
    originalConsoleError.apply(console, args)
  }
  
  console.warn = (...args: unknown[]) => {
    const message = args[0]?.toString() || ''
    // Unterdrücke Firebase WebChannel Warnungen
    if (
      message.includes('webchannel') ||
      message.includes('firestore.googleapis.com')
    ) {
      return // Unterdrücke diese Logs
    }
    originalConsoleWarn.apply(console, args)
  }
}

// Aktiviere Log-Unterdrückung
suppressFirebaseLogs()

const app = createApp(App)

app.use(router)

app.mount('#app')

/**
 * Konvertiert eine relative URL zu einer absoluten URL
 */
function ensureAbsoluteUrl(url: string): string {
  // Wenn die URL bereits absolut ist (beginnt mit http:// oder https://), gib sie zurück
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url
  }
  
  // Wenn die URL mit // beginnt (protokoll-relativ), füge https: hinzu
  if (url.startsWith('//')) {
    return `https:${url}`
  }
  
  // Wenn die URL mit / beginnt (absolut relativ zur Domain), füge die Domain hinzu
  if (url.startsWith('/')) {
    return `${APP_CONFIG.baseUrl}${url}`
  }
  
  // Relative URL - füge die aktuelle Basis-URL hinzu
  const baseUrl = typeof window !== 'undefined' 
    ? `${window.location.protocol}//${window.location.host}${window.location.pathname.replace(/\/[^/]*$/, '/')}`
    : `${APP_CONFIG.baseUrl}/`
  return `${baseUrl}${url}`
}

/**
 * Lädt die Dimensionen eines Bildes
 */
function getImageDimensions(url: string): Promise<{ width: number; height: number }> {
  return new Promise((resolve) => {
    const img = new Image()
    img.onload = () => {
      resolve({ width: img.naturalWidth, height: img.naturalHeight })
    }
    img.onerror = () => {
      // Fallback: Verwende Standard-Dimensionen für Share-Bilder (1200x630)
      resolve({ width: 1200, height: 630 })
    }
    img.src = url
  })
}

/**
 * Setzt die Share-Bild Meta-Tags im <head> der Seite
 */
async function setShareImageMetaTags(url: string) {
  if (typeof document === 'undefined') return

  // Stelle sicher, dass die URL absolut ist
  const absoluteUrl = ensureAbsoluteUrl(url)

  // Aktualisiere Open Graph Image
  let ogImage = document.querySelector('meta[property="og:image"]')
  if (!ogImage) {
    ogImage = document.createElement('meta')
    ogImage.setAttribute('property', 'og:image')
    document.head.appendChild(ogImage)
  }
  ogImage.setAttribute('content', absoluteUrl)

  // Aktualisiere Twitter Image
  let twitterImage = document.querySelector('meta[property="twitter:image"]')
  if (!twitterImage) {
    twitterImage = document.createElement('meta')
    twitterImage.setAttribute('property', 'twitter:image')
    document.head.appendChild(twitterImage)
  }
  twitterImage.setAttribute('content', absoluteUrl)

  // Twitter Card Type
  let twitterCard = document.querySelector('meta[property="twitter:card"]')
  if (!twitterCard) {
    twitterCard = document.createElement('meta')
    twitterCard.setAttribute('property', 'twitter:card')
    document.head.appendChild(twitterCard)
  }
  twitterCard.setAttribute('content', 'summary_large_image')

  // Lade Bilddimensionen und setze og:image:width und og:image:height
  try {
    const dimensions = await getImageDimensions(absoluteUrl)
    
    // Setze og:image:width
    let ogImageWidth = document.querySelector('meta[property="og:image:width"]')
    if (!ogImageWidth) {
      ogImageWidth = document.createElement('meta')
      ogImageWidth.setAttribute('property', 'og:image:width')
      document.head.appendChild(ogImageWidth)
    }
    ogImageWidth.setAttribute('content', dimensions.width.toString())

    // Setze og:image:height
    let ogImageHeight = document.querySelector('meta[property="og:image:height"]')
    if (!ogImageHeight) {
      ogImageHeight = document.createElement('meta')
      ogImageHeight.setAttribute('property', 'og:image:height')
      document.head.appendChild(ogImageHeight)
    }
    ogImageHeight.setAttribute('content', dimensions.height.toString())

    // Setze og:image:type
    let ogImageType = document.querySelector('meta[property="og:image:type"]')
    if (!ogImageType) {
      ogImageType = document.createElement('meta')
      ogImageType.setAttribute('property', 'og:image:type')
      document.head.appendChild(ogImageType)
    }
    // Bestimme den MIME-Type basierend auf der URL
    const extension = absoluteUrl.split('.').pop()?.toLowerCase()
    const mimeType = extension === 'jpg' || extension === 'jpeg' ? 'image/jpeg' 
      : extension === 'png' ? 'image/png'
      : extension === 'webp' ? 'image/webp'
      : 'image/jpeg' // Standard
    ogImageType.setAttribute('content', mimeType)

    // Setze og:image:alt
    let ogImageAlt = document.querySelector('meta[property="og:image:alt"]')
    if (!ogImageAlt) {
      ogImageAlt = document.createElement('meta')
      ogImageAlt.setAttribute('property', 'og:image:alt')
      document.head.appendChild(ogImageAlt)
    }
    ogImageAlt.setAttribute('content', 'Maximilian Reitsberger - Portfolio')
  } catch (error) {
    logger.error('Fehler beim Laden der Bilddimensionen', error)
    // Setze Standard-Dimensionen als Fallback
    const defaultDimensions = { width: 1200, height: 630 }
    
    let ogImageWidth = document.querySelector('meta[property="og:image:width"]')
    if (!ogImageWidth) {
      ogImageWidth = document.createElement('meta')
      ogImageWidth.setAttribute('property', 'og:image:width')
      document.head.appendChild(ogImageWidth)
    }
    ogImageWidth.setAttribute('content', defaultDimensions.width.toString())

    let ogImageHeight = document.querySelector('meta[property="og:image:height"]')
    if (!ogImageHeight) {
      ogImageHeight = document.createElement('meta')
      ogImageHeight.setAttribute('property', 'og:image:height')
      document.head.appendChild(ogImageHeight)
    }
    ogImageHeight.setAttribute('content', defaultDimensions.height.toString())
  }
}

/**
 * Lädt das Favicon aus Firebase und setzt es
 * Wird nach dem Mount ausgeführt, um sicherzustellen, dass Firebase initialisiert ist
 */
async function initializeFavicon() {
  try {
    // Dynamischer Import, um zirkuläre Abhängigkeiten zu vermeiden
    const { getPublicFaviconUrl } = await import('./firebase/favicon')
    const faviconUrl = await getPublicFaviconUrl()
    
    if (faviconUrl) {
      setFavicon(faviconUrl)
    }
  } catch (error) {
    logger.error('Fehler beim Laden des Favicons', error)
    // Fallback: Verwende das Standard-Favicon aus index.html
  }
}

/**
 * Lädt das Share-Bild aus Firebase und setzt die Meta-Tags
 * Wird nach dem Mount ausgeführt, um sicherzustellen, dass Firebase initialisiert ist
 */
async function initializeShareImage() {
  try {
    // Dynamischer Import, um zirkuläre Abhängigkeiten zu vermeiden
    const { getPublicShareImageUrl } = await import('./firebase/shareImage')
    const shareImageUrl = await getPublicShareImageUrl()
    
    if (shareImageUrl) {
      await setShareImageMetaTags(shareImageUrl)
    } else {
      // Kein Share-Bild hochgeladen - verwende Standard-Bild aus index.html
      // Die Meta-Tags bleiben auf dem Standard-Wert
      logger.warn('Kein Share-Bild hochgeladen. Verwende Standard-Bild aus index.html.')
    }
  } catch (error) {
    logger.error('Fehler beim Laden des Share-Bildes', error)
    // Bei Fehler bleiben die Standard-Meta-Tags aus index.html bestehen
  }
}

// Lade Favicon und Share-Bild nach dem Mount der App
// Für Bots und Performance-Tools (PageSpeed) überspringen, um Firestore-Timeouts zu vermeiden
import { shouldSkipFirestore } from './firebase/config'

if (!shouldSkipFirestore) {
  Promise.all([
    initializeFavicon(),
    initializeShareImage()
  ]).catch((error) => {
    logger.error('Fehler beim Initialisieren von Favicon oder Share-Bild', error)
  })
}

